import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectLevelControls,
  selectFillSources,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { clearSectionJobDetails } from "../../../redux/slices/jobDetailsSlice"; // Adjust the path as necessary
import {
  postData,
  deleteData,
} from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import SaveBanner from "./SaveBanner"; // Import SaveBanner
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const FillSources = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "fillSources";

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectLevelControls);
  const fillSourceDetails = useSelector(selectFillSources);
  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const loading = storedJobDetails.length === 0;

  const { formData, setFormData } = useFormState({});

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { handleFocus, handleBlur } = useHandleBlur(formData);

  /****************Add dropdown options here*************************/

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux
  const typeOptions =
    options
      ?.filter((option) => option.DropdownType === "Pond Fill Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options
      ?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  useEffect(() => {
    const filteredSources = fillSourceDetails.filter(
      (source) =>
        source.Zone === activetab && source.ProjectID === jobDetails.ProjectID
    );

    const initialFormData = filteredSources.reduce((acc, source) => {
      acc[source.ID] = source;
      return acc;
    }, {});

    setFormData(initialFormData);
  }, [fillSourceDetails, activetab, jobDetails, setFormData]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  const [, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false); // Add state for notification
  const [saveStatus, setSaveStatus] = useState(""); // Add state for save status

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const handleChange = (e, sourceId) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [sourceId]: {
        ...prevData[sourceId],
        [name]: value,
      },
    }));
  };

  const handleAddFillSource = async () => {
    const newFillSource = {
      projectID: jobId,
      zone: activetab, // Ensure zone is set correctly
      saleQuoteID: jobDetails.SaleQuoteID,
      levelControlID: jobDetails.ID,
      fillSource: jobDetails.FillSource || "",
      type: jobDetails.Type || "",
      hoa: jobDetails.HOA || "",
    };

    // Validate newFillSource object
    if (
      !newFillSource.projectID ||
      !newFillSource.zone ||
      !newFillSource.levelControlID
    ) {
      setError(
        "All required fields must be filled out before adding a level control."
      );
      return;
    }

    try {
      // Post the new drop pipe and refetch job details
      await dispatch(postData({ url: apiUrl, data: newFillSource })).unwrap();

      // Post to change log after successful addition
      const fillSourceDescription = `Fill Source: ${newFillSource.fillSource || "Unknown"}`;
      await dispatch(postChangeLog({
        projectID: jobId,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Fill Sources",
        field: fillSourceDescription,
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();

      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();

      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((source) => {
            if (
              source.Zone === activetab &&
              source.ProjectID === jobDetails.ProjectID
            ) {
              updatedFormData[source.ID] = source;
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to add fill source:", error);
      setError(
        error.message || "An error occurred while adding the fill source."
      );
    }
  };

  const handleDeleteFillSource = async (sourceId) => {
    if (!sourceId) {
      setError("Invalid fill source ID.");
      return;
    }

    try {
      // Find the fill source to delete for change log
      const sourceToDelete = Object.values(formData).find(source => source.ID === sourceId);
      
      // Delete the specified drop pipe and refetch job details
      await dispatch(
        deleteData({ url: apiUrl, deleteId: { ID: sourceId } })
      ).unwrap();

      // Post to change log after successful deletion
      if (sourceToDelete) {
        const fillSourceDescription = `Fill Source: ${sourceToDelete.FillSource || "Unknown"}`;
        await dispatch(postChangeLog({
          projectID: jobId,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Fill Sources",
          field: fillSourceDescription,
          prevValue: "",
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      }

      dispatch(clearSectionJobDetails(sectionName));

      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();

      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((source) => {
            if (
              source.Zone === activetab &&
              source.ProjectID === jobDetails.ProjectID
            ) {
              updatedFormData[source.ID] = source;
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to delete fill source:", error);
      setError(
        error.message || "An error occurred while deleting the fill source."
      );
    }
  };

  const handleBlurWithNotification = async (e, label, options, meta) => {
    await handleBlur(e, label, options, meta);
    setSaveStatus("Saved successfully");
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const fieldClasses = useFieldClass(formData, activetab); // Use useFieldClass hook

  // Helper function to determine the highlight class for a field
  const getFieldHighlightClass = (fieldName, sourceId) => {
    if (fieldClasses?.[sourceId]?.[fieldName]) {
      return fieldClasses[sourceId][fieldName]; // Use fieldClasses for dynamic highlighting
    }
    return ""; // Default class
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  return (
    <div className="fill-sources-responsive">
      <SaveBanner showNotification={showNotification} saveStatus={saveStatus} /> {/* Add SaveBanner */}
      {Object.keys(formData).map((sourceId) => {
        const source = formData[sourceId];

        return (
          <Row key={source.ID} className="mb-3 fill-source-record">
            {/* Fill Source */}
            <Col className="fill-source-field">
              <Form.Group as={Row} controlId={`fillSource-${source.ID}`}>
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("FillSource")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Fill Source</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="FillSource"
                    value={source.FillSource || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlurWithNotification(
                        e,
                        "Fill Source",
                        [],
                        {
                          ID: source.ID,
                          url: apiUrl,
                          field: "FillSource",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={getFieldHighlightClass("FillSource", source.ID)} // Apply dynamic class
                    onChange={(e) => handleChange(e, source.ID)}
                    placeholder="Enter Fill Source"
                  />
                </Col>
              </Form.Group>
            </Col>

            {/* Type */}
            <Col className="fill-source-field">
              <Form.Group as={Row} controlId={`type-${source.ID}`}>
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Type")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="Type"
                    value={source.Type || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlurWithNotification(
                        e,
                        "Type",
                        typeOptions,
                        {
                          ID: source.ID,
                          url: apiUrl,
                          field: "Type",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={getFieldHighlightClass("Type", source.ID)} // Apply dynamic class
                    onChange={(e) => handleChange(e, source.ID)}
                  >
                    <option value="">Select Type</option>
                    {typeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      source.Type,
                      typeOptions
                    ) &&
                      source.Type && (
                        <option
                          value={source.Type}
                          style={{ color: "red" }}
                        >
                          *{source.Type}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            {/* HOA */}
            <Col className="fill-source-field fill-source-field-full-tablet">
              <Form.Group as={Row} controlId={`HOA-${source.ID}`}>
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("HOA")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>HOA</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="HOA"
                    value={source.HOA || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlurWithNotification(
                        e,
                        "HOA",
                        genericOptions,
                        {
                          ID: source.ID,
                          url: apiUrl,
                          field: "HOA",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={getFieldHighlightClass("HOA", source.ID)} // Apply dynamic class
                    onChange={(e) => handleChange(e, source.ID)}
                  >
                    <option value="">Select HOA</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      source.HOA,
                      genericOptions
                    ) &&
                      source.HOA && (
                        <option
                          value={source.HOA}
                          style={{ color: "red" }}
                        >
                          *{source.HOA}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            {/* Delete Button */}
            <Col sm={1} className="fill-source-actions">
              <Button
                variant="link"
                onClick={() => handleDeleteFillSource(source.ID)}
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </Col>
          </Row>
        );
      })}
      <Button
        className="responsive-add-row-button"
        variant="primary"
        onClick={handleAddFillSource}
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
      >
        Add Fill Source
      </Button>
    </div>
  );
};

export default FillSources;
